/**
 * TanStack Query hooks for the SDTM/CDISC terminology library.
 */

import { useQueries, useQuery } from "@tanstack/react-query"
import { terminologyApiService } from "@/services/terminologyApi"

const PAGE_SIZE = 50

const terminologyKeys = {
  all: ["terminology"] as const,
  search: (query: string, offset: number) =>
    [...terminologyKeys.all, "search", query, offset] as const,
  codelist: (code: string) => [...terminologyKeys.all, "codelist", code] as const,
}

/**
 * Searches the terminology library. A page is cached per (query, offset), so paging back and forth
 * or re-opening the modal reuses results.
 */
export function useTerminologySearch(query: string, limit = PAGE_SIZE, offset = 0) {
  return useQuery({
    queryKey: terminologyKeys.search(query, offset),
    queryFn: () => terminologyApiService.search(query, limit, offset),
  })
}

/**
 * Loads `pageCount` consecutive pages of a terminology search as parallel queries, each cached by
 * (query, offset). Callers flatten `.data.items` across the returned results to render the
 * accumulated list, and read `.data.total` from the first page to decide whether more remain.
 */
export function useTerminologySearchPages(query: string, pageCount: number) {
  return useQueries({
    queries: Array.from({ length: pageCount }, (_unused, page) => ({
      queryKey: terminologyKeys.search(query, page * PAGE_SIZE),
      queryFn: () => terminologyApiService.search(query, PAGE_SIZE, page * PAGE_SIZE),
    })),
  })
}

/**
 * Fetches a single codelist (with its full value list) by code. Gated by `enabled` so a row only
 * fetches its values when expanded; the result is cached for the session.
 */
export function useTerminologyCodelist(code: string, enabled: boolean) {
  return useQuery({
    queryKey: terminologyKeys.codelist(code),
    queryFn: () => terminologyApiService.getByCode(code),
    enabled,
  })
}
